/* Bundle Builder — tiered savings with live pacing, per Claude Design handoff (2026-06-10).
   Savings shown here are projections; checkout money-off comes from the BYOB Discount Function
   (one-time; app "ALOHA BYOB Discount Function") and the Skio ladder (autoship, selling plans).
   One-time ATC tags each line with _byob_bundle_id (one UUID per submit) + _byob_qty; the
   Function re-derives units from line quantities grouped by that id and never trusts _byob_qty.
   Autoship lines carry a selling plan and NO _byob properties — the Function skips selling-plan
   lines, so a line can never get both bundle % and Skio %. Distinct submits get distinct UUIDs
   (each tiers independently), so mode-switching between submits can never mix pricing.
   Keep tier settings in sync with the discount's admin UI config (ALO-28). */
(function () {
  'use strict';

  function formatMoney(cents, format) {
    var amount = (cents / 100).toFixed(2);
    var parts = amount.split('.');
    parts[0] = parts[0].replace(/(\d)(?=(\d{3})+$)/g, '$1,');
    return (format || '${{amount}}').replace(/\{\{\s*\w+\s*\}\}/, parts.join('.'));
  }

  function pctLabel(p) { return Math.round(p * 100) + '%'; }

  // mirrors Shopify's handleize: "Mini Bars" -> "mini-bars"
  function handleize(s) {
    return String(s).toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    if (window.crypto && crypto.getRandomValues) {
      var b = crypto.getRandomValues(new Uint8Array(16));
      b[6] = (b[6] & 0x0f) | 0x40;
      b[8] = (b[8] & 0x3f) | 0x80;
      var h = Array.prototype.map.call(b, function (x) { return ('0' + x.toString(16)).slice(-2); }).join('');
      return h.slice(0, 8) + '-' + h.slice(8, 12) + '-' + h.slice(12, 16) + '-' + h.slice(16, 20) + '-' + h.slice(20);
    }
    return 'bb-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  // ALOHA brand check (snippets/svg-icon.liquid 'check') — replaces plain text checkmarks
  var BRAND_CHECK = '<svg class="bb-check" viewBox="0 0 208.91 208.94" width="12" height="12" aria-hidden="true"><path d="M150.2,24.63c-5.99,6.44-11.37.97-17.15-.73C31.64-6.07-23.4,124.06,58.6,176.35c53.3,33.99,125.98-1.13,130.9-63.93.49-6.23-.89-13.51.51-19.52,2.04-8.76,16.31-9.16,18.3-.25.97,4.32.59,17.84.19,22.77-5.94,75.01-92.77,117.35-157.29,79.32C-31.84,145.79-9.21,18.68,85.46,1.38c16.62-3.04,49.29-1.47,63.4,8.7,4.47,3.23,5.13,10.46,1.34,14.54ZM68.51,101.44c2.22,3.22,27.07,28.08,30.05,29.96,5.87,3.68,9.88,1.35,14.48-2.43,31.81-26.19,60.37-64.69,91.99-92.01,9.91-10.26-1.87-24.34-12.98-14.98l-87.23,86.86c-10.06-2.6-24.43-33.02-35.99-20.59-3.37,3.63-2.89,9.47-.31,13.19Z" fill="currentColor"/></svg>';

  function BundleBuilder(root) {
    var configEl = document.querySelector('[data-bb-config="' + root.dataset.bundleBuilder + '"]');
    if (!configEl) return;
    this.cfg = JSON.parse(configEl.textContent);
    this.root = root;
    this.mode = this.resolveInitialMode();
    this.cart = {}; // variantId -> { qty, price, planId }
    this.bonus = {}; // variantId -> { qty: 1, price, bonusPrice, thumb, name } — surprise offer accepts
    this.lastPct = 0;
    this.savingsShown = 0;
    this.el = {};
    var self = this;
    root.querySelectorAll('[data-bb]').forEach(function (n) { self.el[n.dataset.bb] = n; });
    this.bindProducts();
    this.bindModes();
    this.bindCtas();
    this.bindBonus();
    this.bindPresets();
    this.buildFilters();
    this.observeStrip();
    this.syncModeButtons(); // server renders the admin default; a ?mode= param may differ
    this.render();
    this.loadFromUrl();
  }

  BundleBuilder.prototype = {
    // ?mode= param -> Default purchase type setting -> onetime. Autoship only
    // honored when the mode is enabled (otherwise the toggle isn't rendered and
    // lines would silently pick up selling plans).
    resolveInitialMode: function () {
      var mode = this.cfg.defaultMode === 'autoship' ? 'autoship' : 'onetime';
      var raw = new URLSearchParams(location.search).get('mode');
      if (raw) {
        var norm = raw.toLowerCase().replace(/[^a-z]/g, ''); // one-time / one_time -> onetime
        if (norm === 'onetime') mode = 'onetime';
        else if (norm === 'autoship' || norm === 'subscribe') mode = 'autoship';
        this.modeFromUrl = norm === 'onetime' || norm === 'autoship' || norm === 'subscribe';
      }
      if (mode === 'autoship' && !this.cfg.enableAutoship) mode = 'onetime';
      return mode;
    },

    tiers: function (mode) { return this.cfg.modes[mode || this.mode].tiers; },

    pctFor: function (n) {
      var p = 0;
      this.tiers().forEach(function (t) { if (n >= t.minItems) p = t.pct; });
      return p;
    },

    nextTier: function (n) {
      return this.tiers().filter(function (t) { return n < t.minItems; })[0] || null;
    },

    counts: function () {
      var n = 0, sub = 0, lines = [];
      for (var id in this.cart) {
        n += this.cart[id].qty;
        sub += this.cart[id].qty * this.cart[id].price;
        lines.push(this.cart[id].qty * this.cart[id].price);
      }
      var pct = this.pctFor(n);
      var save = this.savingsFor(lines, sub, pct);
      return { n: n, sub: sub, pct: pct, save: save, total: sub - save };
    },

    // One-time: the BYOB Function returns one percentage discount over the bundle lines and
    // Shopify rounds each line's amount DOWN to the cent, so mirror that or the builder shows a
    // few cents more savings than the cart (e.g. $15.90 vs $15.87 on $35.99 + 2x $34.99 at 15%).
    // Autoship (Skio selling plans) keeps the previous whole-subtotal rounding.
    savingsFor: function (lineCents, sub, pct) {
      if (this.mode !== 'onetime') return Math.round(sub * pct);
      return lineCents.reduce(function (sum, c) {
        return sum + Math.floor(Math.round(c * pct * 1000) / 1000); // guard float noise (e.g. 539.99999)
      }, 0);
    },

    // Bonus items live outside the tier ladder: they never count toward milestones or tier savings.
    bonusTotals: function () {
      var sub = 0, save = 0;
      for (var id in this.bonus) {
        sub += this.bonus[id].bonusPrice;
        save += this.bonus[id].price - this.bonus[id].bonusPrice;
      }
      return { sub: sub, save: save };
    },

    money: function (cents) { return formatMoney(cents, this.cfg.moneyFormat); },

    bindProducts: function () {
      var self = this;
      this.cards = {};
      this.root.querySelectorAll('[data-bb-product]').forEach(function (card) {
        var add = card.querySelector('[data-bb-add]');
        if (!add) return; // sold out
        self.cards[card.dataset.variantId] = card;
        add.addEventListener('click', function () { self.changeQty(card, 1); });
        card.querySelector('[data-bb-inc]').addEventListener('click', function () { self.changeQty(card, 1); });
        card.querySelector('[data-bb-dec]').addEventListener('click', function () { self.changeQty(card, -1); });
      });
      // sidebar thumbnails: × removes the whole line
      if (this.el.thumbs) {
        this.el.thumbs.addEventListener('click', function (e) {
          var x = e.target.closest('.bb-thumb__x');
          if (!x) return;
          if (x.dataset.bonusId) { delete self.bonus[x.dataset.bonusId]; self.render(); return; }
          var id = x.dataset.id;
          if (self.cart[id] && self.cards[id]) self.changeQty(self.cards[id], -self.cart[id].qty);
        });
      }
    },

    changeQty: function (card, delta) {
      var id = card.dataset.variantId;
      var entry = this.cart[id] || {
        qty: 0,
        price: parseInt(card.dataset.price, 10),
        planId: card.dataset.planId || '',
        thumb: card.dataset.thumb || '',
        name: card.dataset.name || '',
      };
      entry.qty = Math.max(0, entry.qty + delta);
      if (entry.qty === 0) delete this.cart[id]; else this.cart[id] = entry;
      if (this.preset && !this._prefilling) this.presetEdited();
      if (delta > 0 && !this._prefilling) this.track('bb_item_add', { bb_mode: this.mode, bb_items: this.counts().n });

      var add = card.querySelector('[data-bb-add]');
      var stepper = card.querySelector('.bb-card__stepper');
      var inBundle = entry.qty > 0;
      add.hidden = inBundle;
      stepper.hidden = !inBundle;
      card.classList.toggle('is-in-bundle', inBundle);
      if (inBundle) card.querySelector('[data-bb-qty]').textContent = entry.qty;
      this.afterChange();
    },

    setMode: function (mode) {
      if (mode === 'autoship' && !this.cfg.enableAutoship) mode = 'onetime';
      this.mode = mode;
      this.syncModeButtons();
      this.lastPct = this.counts().pct; // mode switch isn't an "unlock" — no confetti
      this.animateSavings(this.counts().save); // savings tier differs per mode
      this.render();
    },

    syncModeButtons: function () {
      var mode = this.mode;
      this.root.querySelectorAll('[data-bb-mode]').forEach(function (b) {
        var sel = b.dataset.bbMode === mode;
        b.classList.toggle('is-selected', sel);
        b.setAttribute('aria-selected', sel ? 'true' : 'false');
      });
    },

    bindModes: function () {
      var self = this;
      this.root.querySelectorAll('[data-bb-mode]').forEach(function (btn) {
        btn.addEventListener('click', function () { self.setMode(btn.dataset.bbMode); });
      });
      // mode button subtitles
      if (this.el.modeSubOnetime) {
        var ot = this.cfg.modes.onetime.tiers;
        this.el.modeSubOnetime.textContent = 'Save up to ' + pctLabel(ot[ot.length - 1].pct);
      }
      if (this.el.modeSubAutoship && this.cfg.modes.autoship) {
        var as = this.cfg.modes.autoship.tiers;
        this.el.modeSubAutoship.textContent = 'Save up to ' + pctLabel(as[as.length - 1].pct);
      }
    },

    buildFilters: function () {
      var host = this.el.filters;
      if (!host) return;
      var self = this;
      var cats = [];
      this.root.querySelectorAll('[data-bb-product]').forEach(function (card) {
        var c = card.dataset.category;
        if (c && cats.indexOf(c) === -1) cats.push(c);
      });
      if (cats.length < 2) return; // nothing to filter
      host.hidden = false;
      host.innerHTML = ['All'].concat(cats).map(function (c, i) {
        return '<button type="button" class="bb-filter' + (i === 0 ? ' is-selected' : '') + '" data-filter="' + c + '">' + c + '</button>';
      }).join('');
      function applyFilter(pick) {
        host.querySelectorAll('.bb-filter').forEach(function (b) { b.classList.toggle('is-selected', b.dataset.filter === pick); });
        self.root.querySelectorAll('[data-bb-product]').forEach(function (card) {
          card.hidden = pick !== 'All' && card.dataset.category !== pick;
        });
      }
      host.addEventListener('click', function (e) {
        var btn = e.target.closest('.bb-filter');
        if (!btn) return;
        applyFilter(btn.dataset.filter);
      });
      // ?filter= deep link: handleized match, so ?filter=mini-bars selects "Mini Bars".
      // No match (or ?filter=all) leaves the "All" pill selected.
      var want = new URLSearchParams(location.search).get('filter');
      if (want) {
        var match = cats.filter(function (c) { return handleize(c) === handleize(want); })[0];
        if (match) applyFilter(match);
      }
    },

    bindCtas: function () {
      var self = this;
      this.root.querySelectorAll('[data-bb-cta]').forEach(function (btn) {
        btn.addEventListener('click', function () { self.addBundle(btn); });
      });
    },

    observeStrip: function () {
      var strip = (this.el.strip && this.el.strip.parentElement) || this.el.stack; // stack hero has no strip
      var bar = this.el.bar;
      if (!strip || !bar || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(function (entries) {
        var e = entries[0];
        var show = !e.isIntersecting && e.boundingClientRect.bottom <= 0;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      }, { threshold: 0 }).observe(strip);
    },

    afterChange: function () {
      var c = this.counts();
      if (c.pct > this.lastPct) this.celebrate();
      this.lastPct = c.pct;
      this.animateSavings(c.save);
      this.render();
    },

    animateSavings: function (target) {
      cancelAnimationFrame(this._raf);
      var self = this;
      var from = this.savingsShown;
      var t0 = performance.now();
      function step(now) {
        var k = Math.min(1, (now - t0) / 600);
        var e = 1 - Math.pow(1 - k, 3);
        self.savingsShown = from + (target - from) * e;
        self.paintSavings();
        if (k < 1) self._raf = requestAnimationFrame(step);
      }
      this._raf = requestAnimationFrame(step);
    },

    paintSavings: function () {
      var savedCents = Math.round(this.savingsShown) + this.bonusTotals().save;
      var txt = this.money(savedCents);
      if (this.el.savings) this.el.savings.textContent = txt;
      if (this.el.mobileSave) {
        this.el.mobileSave.textContent = txt;
        var saveWrap = this.el.mobileSave.closest('.bb-mobile__save');
        if (saveWrap) saveWrap.classList.toggle('is-zero', savedCents <= 0);
      }
      var c = this.counts();
      if (this.el.barRight) {
        this.el.barRight.textContent = (c.n > 0 && c.pct > 0) ? "You're saving " + txt : this.nudgeCopy(c);
      }
    },

    celebrate: function (hosts) {
      // Hawaiian burst built from the ALOHA brand pattern SVGs (Dark Green_Pattern.svg):
      // four-petal bloom, palm-fan cluster, feathered leaf, solid leaf, seed — recolored per palette
      var colors = ['#00ce7c', '#f2bd19', '#ff8300', '#9bcba3', '#2a4636'];
      var shapes = [
        { vb: '185.1 116.8 89.3 70.7', d: 'M239.2,170.2c2,1.2,4.4,1.9,6.8,1.9c2.4,0,4.7-0.4,7-1c6.5-1.6,12.6-4.1,18.4-7.5c-5.5-4.1-11-8.3-17.2-11.2 s-13.3-4.4-19.9-3l-0.4-0.3c4.6-0.1,9.2-0.9,13.6-2.3c2.9-0.9,5.6-2.1,8.1-3.8c7.2-5.1,10.6-14,12.8-22.7 c-10.1,0.9-18.9,2.2-28.1,9.4c2,5.7,3,11.3-2.5,16.1c-2.1,2-4.7,3.5-7.5,4.4c0,0.1,0,0.2,0,0.3c0-0.1-0.1-0.2-0.1-0.2 c-0.7,0.2-1.4,0.4-2.1,0.5c-2.2,0.3-6.2,0.6-8.4-0.5c-0.9-0.3-1.7-0.7-2.5-1.3c-1.7-1.8-4-3.8-4.7-6.2c-0.4-1.4,1-3,0.4-4.4 c-2.5-6.3-0.8-9.5-0.6-9.7c-0.3-0.3-0.6-0.6-0.9-0.9c-3.5-2.8-7.4-5.1-11.6-6.7c-2-0.8-4.2-1.3-6.4-1.3c-1.8,0-3.6,0.5-5.3,0.2 c0.8,5.7,2.7,11.2,5.5,16.2c2.8,5,7,9.1,12.2,11.7c1.9,0.9,3.9,1.5,5.9,1.9c-0.4,0.3-0.8,0.5-1.2,0.9c-1.6,1.3-3.1,2.7-4.3,4.4 c-3,4-3.8,10.3-3.4,15.3c0.4,5,2,9.9,4.7,14.1c0.5-1.3,1.4-2.4,2.5-3.1c1.2-0.7,2.4-1.3,3.6-1.9c3.1-1.3,6-3.3,8.4-5.7 c2.7-2.9,4.8-6.2,6.4-9.8c0.7-1.6,1.3-2.9,2-4.5c0.3-0.9,0.5-1.7,0.6-2.6c0.8,2.6,1.9,5.1,3.4,7.4 C235.4,166.6,237.1,168.6,239.2,170.2' },
        { vb: '343 304.8 98.6 77.1', d: 'M346,332.3c25.4,2.3,49.4,19.2,60.8,42.8c-9.2-19.9-11.4-42.3-6.5-63.6c6.3,18.2,9.1,37.4,8.2,56.7 c1.4-7.3,2.9-14.6,4.3-21.9c1-5.4,2.1-10.8,4.2-15.8c3.7-8.9,10.4-15.9,17-22.7c-2.6,10.6-5.2,21.1-7.8,31.7 c-2.2,9-4.6,18.4-10.5,25.4c-4,4.8-9.6,8.4-12.3,14c11.7-13.6,23.5-27.1,35.2-40.7c-1.5,9.4-6.2,18-13.3,24.3 c-14.9,13.2-20.6,13.6-35.8,2.2C380.1,357.6,357.1,333.3,346,332.3' },
        { vb: '462.2 670.3 65 42.5', d: 'M523,673.3l-0.7,0.8l0.7,1.2c-0.2,0.7-0.4,1.3-0.6,2c-0.4,1.5-1.8,2.7-2.9,3.8c-2.3,2.2-4.8,4.2-7.5,6 c-2.7,1.8-5.4,3.3-8.3,4.7c-2.7,1.3-5.5,2.2-8.2,3.4c-0.9,0.4-1.7,1-2.5,1.5c-0.7,0.4-1.4,0.6-2.1,0.6c-1.2,0.1,1-1.9,0.9-1.8 c2.4-1.9,5.1-3.5,7.7-5.2c2-1.3,4-2.5,6.1-3.8c1.7-0.8,3.1-2.1,4.1-3.7c0.1-0.2,0-0.4-0.1-0.5c-0.1,0-0.2-0.1-0.3,0 c-1.9,0.3-3.4,1.5-5,2.4c-2.4,1.4-4.9,2.7-7.2,4s-4.8,2.8-7.1,4.3c-1.7,1.1-3.8,3.1-5.9,3.5c0.3-2.4,2.5-4.5,4.1-6.2 c1.2-1.3,2.4-2.7,3.7-3.9c-3.7,2.2-7,4.8-10.1,7.8c-5.5,6-8.2,9.6-10.7,11.9c-1.8,1.5-3.8,2.8-5.9,3.7c2.9,0.2,5.7-0.3,8.4-1.4 c8.3-3.3,16.5-7.1,24.4-11.4c5.5-3.2,16.5-8.7,19.2-11s6.6-6.3,6.6-7.8c0.1-0.7,0-1.3-0.2-2l0.6-0.9L523,673.3z' },
        { vb: '523.2 655.1 55.6 29.5', d: 'M526.2,678.6l1.4-0.9c0,0-1.8-3.3,2.3-5.4s19.3-8.8,23.8-10.2s15.2-3.5,17.4-3.9c1.6-0.2,3.1-0.2,4.7,0.1 c0,0-4.3,3.6-7.9,6.2s-18.6,11.3-23.2,13.1s-13.7,4.8-14.8,3.8c-0.5-0.5-1-1-1.5-1.6l-1.4,0.8L526.2,678.6z' },
        { vb: '304.5 421 28.3 30.1', d: 'M312.2,425.9c-1.4,2.8-2.6,5.7-3.5,8.7c-1.1,3-1.7,5.9-0.5,8.9c2,5,9.1,5.5,14.4,3.5c2-0.5,3.8-1.4,5.3-2.8 c3-2.6,2.1-6.3,0.1-9.3c-2-3.4-6.1-6.1-8.8-8.5c-0.5-0.5-1.1-0.9-1.7-1.3C315.6,423.7,313.7,423.2,312.2,425.9 M318.2,444.1 c2.3-0.1,5.6-0.4,7.9-1.7c0.2-0.2,0.4-0.3,0.6-0.5c0.1,0,0.1,0.1,0.2,0.1l-0.1,0.1c-3,4-10.2,4.3-14.5,3.2 c-2.5-0.6-3.4-2.1-3.3-3.9C310.2,444.2,315.1,444.2,318.2,444.1' }
      ];
      (hosts || ['confettiBar', 'confettiMobile']).forEach(function (key) {
        var host = this.el[key];
        if (!host || host.offsetParent === null) return;
        host.innerHTML = '';
        for (var i = 0; i < 16; i++) {
          var sh = shapes[i % shapes.length];
          var s = document.createElement('span');
          var ang = (i / 16) * Math.PI * 2;
          var dist = 60 + (i % 5) * 26;
          var size = 12 + (i % 4) * 4;
          s.innerHTML = '<svg width="' + size + '" height="' + size + '" viewBox="' + sh.vb + '" preserveAspectRatio="xMidYMid meet">' +
            '<path d="' + sh.d + '" fill="' + colors[i % colors.length] + '"/></svg>';
          s.style.cssText = 'position:absolute;left:50%;top:12px;pointer-events:none;line-height:0;' +
            '--dx:' + (Math.cos(ang) * dist) + 'px;--dy:' + (Math.sin(ang) * dist - 40) + 'px;--rot:' + (((i * 53) % 360) - 180) + 'deg;' +
            'animation: bb-confetti 0.8s ease-out forwards;';
          host.appendChild(s);
        }
        setTimeout(function () { host.innerHTML = ''; }, 1000);
      }, this);
      this._popping = true;
      var self = this;
      setTimeout(function () { self._popping = false; }, 600);
    },

    milestoneData: function (c) {
      var tiers = this.tiers();
      var maxMin = tiers[tiers.length - 1].minItems;
      var base = (this.mode === 'autoship' ? [] : [{ minItems: 1, pct: 0, label: 'Start' }])
        .concat(tiers.map(function (t) { return { minItems: t.minItems, pct: t.pct, label: pctLabel(t.pct) + ' off' }; }));
      var reached = base.filter(function (m) { return c.n >= m.minItems; }).length;
      return {
        list: base.map(function (m, i) {
          return {
            label: m.label,
            sub: m.minItems >= maxMin ? m.minItems + '+ items' : m.minItems + (m.minItems === 1 ? ' item' : ' items'),
            reached: c.n >= m.minItems,
            current: c.n >= m.minItems && i === reached - 1,
            pop: this._popping && c.pct === m.pct && c.n >= m.minItems,
          };
        }, this),
        progressPct: base.length > 1 ? (Math.max(0, reached - 1) / (base.length - 1)) * 100 : 0,
      };
    },

    // A/B "Stack" hero: title and sub follow the selected purchase type.
    renderStack: function () {
      var el = this.el;
      var tiers = this.tiers();
      var top = tiers[tiers.length - 1];
      if (el.stackTitle) el.stackTitle.textContent = (this.cfg.stackHeading || '').replace('{pct}', pctLabel(top.pct));
      if (el.stackSub) {
        el.stackSub.textContent = this.mode === 'autoship'
          ? 'Savings start with your first item. Delivered Every Month.'
          : 'Savings start at ' + tiers[0].minItems + ' items. Ships once, no recurring billing.';
      }
    },

    nudgeCopy: function (c) {
      var tiers = this.tiers();
      var next = this.nextTier(c.n);
      if (c.n === 0) {
        return this.mode === 'autoship'
          ? 'Add your first item to start saving!'
          : 'Add ' + tiers[0].minItems + ' items to unlock ' + pctLabel(tiers[0].pct) + ' off.';
      }
      if (next) {
        var lead = c.pct > 0 ? 'Nice. ' + pctLabel(c.pct) + ' off unlocked. ' : '';
        return lead + 'Add ' + (next.minItems - c.n) + ' more to unlock ' + pctLabel(next.pct) + ' off.';
      }
      return "Best value unlocked. You're saving " + this.money(c.save) + ' at ' + pctLabel(c.pct) + ' off.';
    },

    render: function () {
      var c = this.counts();
      var cfg = this.cfg;
      var mode = cfg.modes[this.mode];
      var isAuto = this.mode === 'autoship';
      var el = this.el;
      var self = this;

      // savings strip (copy is merchant-editable via the section schema)
      var strip = cfg.strip || {};
      if (el.strip) {
        var cols = [];
        if (!isAuto) {
          cols.push({ over: '1 item', big: strip.startTitle, note: (strip.startNote || '').replace('{count}', mode.tiers[0].minItems), unlocked: false });
        }
        var maxMin = mode.tiers[mode.tiers.length - 1].minItems;
        mode.tiers.forEach(function (t) {
          cols.push({
            over: (t.minItems >= maxMin ? t.minItems + '+ items' : t.minItems + (t.minItems === 1 ? ' item' : ' items')),
            big: pctLabel(t.pct) + ' Off',
            note: t.best ? strip.bestNote : (t.minItems === 1 ? strip.firstNote : strip.tierNote),
            unlocked: c.n >= t.minItems,
          });
        });
        el.strip.innerHTML = cols.map(function (col) {
          return '<div class="bb-strip__col">' +
            '<div class="bb-strip__overline">' + col.over + '</div>' +
            '<div class="bb-strip__big' + (col.unlocked ? ' is-unlocked' : '') + '">' + col.big + '</div>' +
            '<div class="bb-strip__note' + (col.unlocked ? ' is-unlocked' : '') + '">' + (col.unlocked ? BRAND_CHECK + ' ' + strip.unlockedLabel : col.note) + '</div>' +
            '</div>';
        }).join('');
      }

      // milestones (desktop bar + mobile bar share data)
      var ms = this.milestoneData(c);
      var msHtml = ms.list.map(function (m) {
        return '<div class="bb-ms' + (m.reached ? ' is-reached' : '') + (m.current ? ' is-current' : '') + (m.pop ? ' is-popping' : '') + '">' +
          '<div class="bb-ms__pill">' + m.label + '</div>' +
          '<div class="bb-ms__sub">' + m.sub + '</div>' +
          '</div>';
      }).join('');
      if (el.barMilestones) el.barMilestones.innerHTML = msHtml;
      if (el.mobileMilestones) el.mobileMilestones.innerHTML = msHtml;
      if (el.barFill) el.barFill.style.width = ms.progressPct + '%';
      if (el.mobileFill) el.mobileFill.style.width = ms.progressPct + '%';
      if (el.barTitle) el.barTitle.textContent = mode.label;

      // free shipping
      var fsT = cfg.freeShippingThreshold;
      var freeShip = c.n > 0 && c.total >= fsT;
      var fsRem = Math.max(0, fsT - c.total);
      if (el.shipValue) {
        el.shipValue.textContent = freeShip ? 'Free' : 'Free over ' + this.money(fsT);
        el.shipValue.classList.toggle('is-free', freeShip);
      }
      if (el.shipNudge) {
        el.shipNudge.hidden = !(c.n > 0 && !freeShip);
        el.shipNudge.textContent = 'Add ' + this.money(fsRem) + ' more for free shipping.';
      }
      if (el.barShip) {
        el.barShip.innerHTML = c.n > 0 ? (freeShip ? BRAND_CHECK + ' Free shipping unlocked' : this.money(fsRem) + ' away from free shipping') : '';
        el.barShip.classList.toggle('is-free', freeShip);
      }

      // sidebar bundle contents (compact thumbnails + qty, × to remove; bonus items styled apart)
      var bt = this.bonusTotals();
      if (el.thumbs) {
        var ids = Object.keys(this.cart);
        var bids = Object.keys(this.bonus);
        el.thumbs.hidden = ids.length + bids.length === 0;
        el.thumbs.innerHTML = ids.map(function (id) {
          var it = self.cart[id];
          return '<div class="bb-thumb" title="' + it.name + '">' +
            '<img src="' + it.thumb + '" alt="' + it.name + '">' +
            '<span class="bb-thumb__qty">' + it.qty + '</span>' +
            '<button type="button" class="bb-thumb__x" data-id="' + id + '" aria-label="Remove ' + it.name + '">&times;</button>' +
            '</div>';
        }).join('') + bids.map(function (id) {
          var it = self.bonus[id];
          return '<div class="bb-thumb bb-thumb--bonus" title="' + it.name + ' (bonus)">' +
            '<img src="' + it.thumb + '" alt="' + it.name + '">' +
            '<span class="bb-thumb__qty">★</span>' +
            '<button type="button" class="bb-thumb__x" data-bonus-id="' + id + '" aria-label="Remove bonus ' + it.name + '">&times;</button>' +
            '</div>';
        }).join('');
      }

      // sidebar
      if (el.itemCount) el.itemCount.textContent = c.n;
      if (el.subtotal) el.subtotal.textContent = this.money(c.sub);
      if (el.pctLabel) el.pctLabel.textContent = c.pct > 0 ? pctLabel(c.pct) + ' Savings' : '0%';
      if (el.discountRowLabel) el.discountRowLabel.textContent = mode.discountRowLabel;
      if (el.freqRow) el.freqRow.hidden = !isAuto;
      if (el.bonusRow) {
        el.bonusRow.hidden = bt.save === 0;
        if (el.bonusSave) el.bonusSave.textContent = '−' + this.money(bt.save);
      }
      if (el.total) el.total.textContent = this.money(c.total + bt.sub);
      if (el.mobileTotal) el.mobileTotal.textContent = this.money(c.total + bt.sub);
      if (el.discountPill) {
        el.discountPill.textContent = c.pct > 0 ? pctLabel(c.pct) + ' off unlocked' : 'No discount yet';
        el.discountPill.classList.toggle('is-unlocked', c.pct > 0);
      }
      if (el.summaryNote) {
        var trustItems = isAuto ? ['Delivers every 1 month', 'Skip or cancel anytime'] : ['One-time purchase', 'No recurring charges or billing'];
        el.summaryNote.innerHTML = trustItems.map(function (t) {
          return '<span class="bb-side__trust-item">' + BRAND_CHECK + '<span>' + t + '</span></span>';
        }).join('');
      }
      this.renderPresetCards();
      if (cfg.heroStyle === 'stack') this.renderStack();
      if (el.heroNote) el.heroNote.textContent = isAuto ? 'Delivered on your schedule. Skip or cancel anytime.' : 'It ships once. No recurring charges or billing.';

      // CTAs
      this.root.querySelectorAll('[data-bb-cta]').forEach(function (btn) { btn.disabled = c.n === 0; });
      if (el.cta) el.cta.textContent = mode.cta;
      if (el.ctaShort) el.ctaShort.textContent = mode.ctaShort;

      // mobile status line
      if (el.mobileSub) {
        // Compact by design: must fit one line on a 320px viewport, no truncation
        el.mobileSub.textContent = (c.n === 1 ? '1 item' : c.n + ' items') +
          (c.pct > 0 ? ' · ' + pctLabel(c.pct) + ' off' : '') +
          (freeShip ? ' · ships free' : (c.n > 0 ? ' · ' + this.money(fsRem) + ' to free ship' : ''));
      }

      this.paintSavings();
    },

    // ----- Starter bundles (ALO-99) -----
    // Presets pre-fill the builder through changeQty, so steppers, tiers and confetti behave
    // exactly as if the shopper had tapped each item. Liquid already dropped sold-out slots and
    // presets under the minimum; here items without a grid card (product outside the builder
    // collection) are dropped too, since only carded variants can be edited.
    bindPresets: function () {
      var self = this;
      var min = this.cfg.presetMinItems || 3;
      this.presets = {};
      var notGrid = {}; // handle -> count of products with no grid card (editor note only)
      (this.cfg.presets || []).forEach(function (p) {
        var items = {}, units = 0, dropped = p.dropped || 0;
        p.items.forEach(function (it) {
          if (!self.cards[it.id]) { dropped++; notGrid[p.handle] = (notGrid[p.handle] || 0) + 1; return; }
          items[it.id] = (items[it.id] || 0) + it.qty;
          units += it.qty;
        });
        if (units < min) return;
        self.presets[p.handle] = { handle: p.handle, name: p.name, mode: p.mode, items: items, units: units, dropped: dropped };
      });

      var wrap = this.el.presets;
      if (!wrap) return;
      wrap.querySelectorAll('[data-bb-preset]').forEach(function (card) {
        var p = self.presets[card.dataset.bbPreset];
        if (!p) {
          // Theme editor: keep the card visible and say why shoppers won't see it
          if (window.Shopify && Shopify.designMode) {
            var n = notGrid[card.dataset.bbPreset] || 0;
            card.querySelectorAll('button').forEach(function (b) { b.disabled = true; });
            card.classList.add('is-editor-hidden');
            var cta = card.querySelector('.bb-preset__cta');
            if (cta) cta.textContent = 'Hidden: ' + n + (n === 1 ? ' product isn\u2019t' : ' products aren\u2019t') + ' in the builder grid, under ' + min + ' items';
            return;
          }
          card.hidden = true;
          return;
        }
        card.querySelectorAll('[data-bb-preset-item]').forEach(function (t) {
          if (!p.items[t.dataset.bbPresetItem]) t.hidden = true;
        });
        // Buttons carry the action (fill the builder / add straight to cart). A click elsewhere on
        // the card fills, but only when the card offers that; an add-only card needs the button.
        card.addEventListener('click', function (e) {
          var btn = e.target.closest('[data-bb-preset-action]') || card.querySelector('[data-bb-preset-action="fill"]');
          if (!btn || btn.disabled || self._adding) return;
          var action = btn.dataset.bbPresetAction;
          self.track('bb_preset_card_click', { bb_preset: p.handle, bb_preset_action: action });
          self.requestPreset(p, action, btn);
        });
      });
      if (!wrap.querySelector('[data-bb-preset]:not([hidden])') && !wrap.querySelector('.bb-presets__editor-note')) wrap.hidden = true;

      var confirm = this.el.presetConfirm;
      if (!confirm) return;
      function act(sel, fn) {
        var b = confirm.querySelector(sel);
        if (b) b.addEventListener('click', fn);
      }
      act('[data-bb-preset-replace]', function () { self.applyPreset(self._pendingPreset, true, 'card'); });
      act('[data-bb-preset-merge]', function () { self.applyPreset(self._pendingPreset, false, 'card'); });
      act('[data-bb-preset-cancel]', function () { self.hidePresetConfirm(); });
    },

    // Card click: an empty bundle loads straight away; otherwise ask before touching the build.
    requestPreset: function (p, action, btn) {
      this._pendingAction = { action: action, btn: btn };
      if (this.counts().n === 0) { this.applyPreset(p, true, 'card'); return; }
      var confirm = this.el.presetConfirm;
      if (!confirm) { this.applyPreset(p, true, 'card'); return; }
      this._pendingPreset = p;
      this.el.presetConfirmText.textContent = 'Replace your current bundle with ' + p.name + '?';
      confirm.hidden = false;
      confirm.querySelector('[data-bb-preset-replace]').focus();
    },

    hidePresetConfirm: function () {
      this._pendingPreset = null;
      if (this.el.presetConfirm) this.el.presetConfirm.hidden = true;
    },

    applyPreset: function (p, replace, source) {
      if (!p) return;
      var self = this;
      this.hidePresetConfirm();
      this._prefilling = true;
      if (replace) {
        Object.keys(this.cart).forEach(function (id) {
          if (self.cards[id]) self.changeQty(self.cards[id], -self.cart[id].qty);
        });
      }
      // Preset purchase type applies to direct links only; ?mode= wins, and a card click keeps
      // whatever the shopper already picked on the toggle.
      if (source === 'link' && !this.modeFromUrl && (p.mode === 'onetime' || p.mode === 'autoship')) this.setMode(p.mode);
      Object.keys(p.items).forEach(function (id) { self.changeQty(self.cards[id], p.items[id]); });
      this._prefilling = false;
      this.preset = { handle: p.handle, name: p.name, dropped: p.dropped, edited: false };
      this.renderPresetBanner();
      var pending = source === 'card' ? this._pendingAction : null;
      this._pendingAction = null;
      this.track('bb_preset_loaded', {
        bb_preset: p.handle,
        bb_preset_source: source,
        bb_mode: this.mode,
        bb_units: p.units,
        bb_preset_dropped: p.dropped,
      });
      // "Add straight to cart": same submit as the summary CTA (bundle discount, bonus offer),
      // with the builder already showing what was added.
      if (pending && pending.action === 'add') this.addBundle(pending.btn);
      else this.revealSummary();
    },

    // ?bundle=<handle> (curated) or ?items=variantId:qty,... (ad-hoc, for CX). The URL is never
    // rewritten, so UTMs pass through untouched. Unknown handles load the normal empty builder.
    loadFromUrl: function () {
      var params = new URLSearchParams(location.search);
      var handle = params.get('bundle');
      if (handle) {
        var p = this.presets && this.presets[handleize(handle)];
        if (p) this.applyPreset(p, true, 'link');
        return;
      }
      var raw = params.get('items');
      if (!raw) return;
      var items = {}, units = 0, dropped = 0, self = this;
      raw.split(',').forEach(function (pair) {
        var bits = pair.split(':');
        var id = String(parseInt(bits[0], 10));
        var qty = Math.min(12, Math.max(1, parseInt(bits[1], 10) || 1));
        if (!self.cards[id]) { dropped++; return; }
        items[id] = (items[id] || 0) + qty;
        units += qty;
      });
      if (units === 0) return;
      this.applyPreset({ handle: 'custom', name: '', mode: 'inherit', items: items, units: units, dropped: dropped }, true, 'adhoc');
    },

    presetEdited: function () {
      if (this.counts().n === 0) { // emptied the build: it's no longer this preset
        this.preset = null;
        this.renderPresetBanner();
        return;
      }
      if (this.preset.edited) return;
      this.preset.edited = true;
      this.track('bb_preset_item_edited', { bb_preset: this.preset.handle });
    },

    renderPresetBanner: function () {
      var p = this.preset;
      var self = this;
      [this.el.presetBanner, this.el.mobilePreset].forEach(function (host) {
        if (!host) return;
        host.hidden = !p;
        if (!p) return;
        host.querySelector('[data-bb-preset-name]').textContent =
          (self.cfg.presetBannerTitle || '{name} is ready').replace('{name}', p.name || 'Your bundle');
        var notice = host.querySelector('[data-bb-preset-notice]');
        if (!notice) return;
        notice.hidden = !p.dropped;
        notice.textContent = p.dropped === 1
          ? '1 item is unavailable right now and was left out.'
          : p.dropped + ' items are unavailable right now and were left out.';
      });
    },

    // Bring the summary + CTA into view and pulse it so the one-tap add is obvious.
    revealSummary: function () {
      var side = this.root.querySelector('.bb-side');
      var targets = [this.root.querySelector('.bb-side__card'), this.el.mobileBar];
      targets.forEach(function (t) {
        if (!t) return;
        t.classList.remove('is-flash');
        void t.offsetWidth; // restart the animation
        t.classList.add('is-flash');
      });
      if (!side || side.offsetParent === null) return; // mobile: the docked bar is always visible
      var main = this.root.querySelector('.bb-main');
      var r = main.getBoundingClientRect();
      if (r.top > window.innerHeight * 0.5 || r.bottom < 200) {
        var offset = parseInt(getComputedStyle(side).top, 10) || 96;
        window.scrollTo({ top: r.top + window.pageYOffset - offset, behavior: 'smooth' });
      }
    },

    // Card price + savings follow the selected mode, so they always match the sidebar.
    renderPresetCards: function () {
      if (!this.presets || !this.el.presets) return;
      var self = this;
      this.el.presets.querySelectorAll('[data-bb-preset]').forEach(function (card) {
        var p = self.presets[card.dataset.bbPreset];
        if (!p) return;
        var sub = 0, lines = [];
        for (var id in p.items) {
          var line = p.items[id] * parseInt(self.cards[id].dataset.price, 10);
          sub += line;
          lines.push(line);
        }
        var pct = self.pctFor(p.units);
        var save = self.savingsFor(lines, sub, pct);
        card.querySelector('[data-bb-preset-count]').textContent = p.units + ' items';
        card.querySelector('[data-bb-preset-price]').textContent = self.money(sub - save);
        var compare = card.querySelector('[data-bb-preset-compare]');
        compare.textContent = save > 0 ? self.money(sub) : '';
        card.querySelector('[data-bb-preset-save]').textContent = save > 0
          ? 'Save ' + self.money(save) + ' · ' + pctLabel(pct) + ' off' + (self.mode === 'autoship' ? ' with Autoship' : '')
          : '';
      });
    },

    track: function (event, data) {
      var payload = { event: event, bb_hero: this.cfg.heroStyle || 'classic' }; // Shoplift arm
      for (var k in data) payload[k] = data[k];
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    },

    // ----- Surprise & delight bonus interstitial -----
    bindBonus: function () {
      var modal = this.el.bonusModal;
      if (!modal) return;
      var self = this;
      this.bonusCards = Array.prototype.slice.call(modal.querySelectorAll('[data-bb-bonus]'));
      modal.querySelectorAll('[data-bb-bonus-skip]').forEach(function (b) {
        b.addEventListener('click', function () { self.closeBonus(true); });
      });
      this.bonusCards.forEach(function (card) {
        card.querySelector('[data-bb-bonus-add]').addEventListener('click', function () {
          self.bonus[card.dataset.variantId] = {
            qty: 1,
            price: parseInt(card.dataset.price, 10),
            bonusPrice: parseInt(card.dataset.bonusPrice, 10),
            thumb: card.dataset.thumb || '',
            name: card.dataset.name || '',
          };
          self.closeBonus(true);
        });
      });
      modal.addEventListener('keydown', function (e) { if (e.key === 'Escape') self.closeBonus(true); });
    },

    // Valid bonus offers hide anything already in the shopper's build (or an already-claimed
    // bonus) — no double-dipping on one variant. Grid products ARE allowed as bonuses: the
    // bonus line carries different properties, so /cart/add.js keeps it separate from bundle
    // lines and the Function prices each correctly.
    validBonusCards: function () {
      var self = this;
      return (this.bonusCards || []).filter(function (card) {
        var id = card.dataset.variantId;
        return !self.cart[id] && !self.bonus[id];
      });
    },

    bonusEligible: function () {
      var b = this.cfg.bonus;
      if (!b || !b.enabled || !this.el.bonusModal) return false;
      // Both modes qualify; the bonus LINE is always added one-time (no selling plan),
      // so on autoship builds it ships once with the first order and never recurs.
      if (this.counts().n < b.minItems) return false;
      try { if (sessionStorage.getItem('bb:bonus:shown')) return false; } catch (e) {}
      return this.validBonusCards().length > 0;
    },

    openBonus: function (btn) {
      var modal = this.el.bonusModal;
      this._pendingCta = btn;
      try { sessionStorage.setItem('bb:bonus:shown', '1'); } catch (e) {}
      var valid = this.validBonusCards();
      this.bonusCards.forEach(function (card) { card.hidden = valid.indexOf(card) === -1; });
      modal.hidden = false;
      requestAnimationFrame(function () { modal.classList.add('is-open'); });
      this.celebrate(['confettiModal']);
      var skip = modal.querySelector('.bb-bonus__skip');
      if (skip) skip.focus();
    },

    closeBonus: function (proceed) {
      var modal = this.el.bonusModal;
      var self = this;
      modal.classList.remove('is-open');
      setTimeout(function () { modal.hidden = true; }, 250);
      this.render();
      if (proceed && this._pendingCta) {
        var btn = this._pendingCta;
        this._pendingCta = null;
        this.submitBundle(btn);
      }
    },

    addBundle: function (btn) {
      var c = this.counts();
      if (c.n === 0 || this._adding) return;
      if (this.preset) {
        this.track('bb_preset_atc', {
          bb_preset: this.preset.handle,
          bb_preset_edited: this.preset.edited,
          bb_units: c.n,
          bb_mode: this.mode,
          bb_via: btn && btn.closest && btn.closest('[data-bb-preset]') ? 'card' : 'summary',
        });
      }
      if (this.bonusEligible()) { this.openBonus(btn); return; } // surprise before checkout
      this.submitBundle(btn);
    },

    submitBundle: function (btn) {
      var c = this.counts();
      if (c.n === 0 || this._adding) return;
      this._adding = true;
      var isAuto = this.mode === 'autoship';
      var items = [];
      // One-time only: hidden properties scope the BYOB Discount Function to builder
      // lines (never PDP/collection adds). One UUID per submit; the Function groups
      // lines by it and counts units server-side. Autoship lines get a selling plan
      // and no properties — the Function always skips selling-plan lines.
      var bundleId = uuid();
      for (var id in this.cart) {
        var item = { id: parseInt(id, 10), quantity: this.cart[id].qty };
        if (isAuto) {
          if (this.cart[id].planId) item.selling_plan = parseInt(this.cart[id].planId, 10);
        } else {
          item.properties = { _byob_bundle_id: bundleId, _byob_qty: String(c.n) };
        }
        items.push(item);
      }
      var bonusMin = this.cfg.bonus ? this.cfg.bonus.minItems : 0;
      for (var bid in this.bonus) {
        // hidden property marks the line so the cart drawer can drop it if the bundle shrinks
        items.push({ id: parseInt(bid, 10), quantity: 1, properties: { _bb_bonus: String(bonusMin) } });
      }
      var original = btn.textContent;
      btn.textContent = 'Adding…';
      var self = this;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items }),
      })
        .then(function (r) {
          if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || 'Could not add bundle'); });
          return r.json();
        })
        .then(function () {
          // Hidden cart attribute lands on the (first) order so Shopify Flow can
          // tag builder orders (ALO-28 analytics): _byob_onetime for one-time,
          // _byob_autoship for builder-started subscriptions. Cart attributes do
          // NOT recur on Skio renewal orders — first-order signal only. Underscore
          // keeps them out of customer-facing order details.
          var attrs = {};
          attrs[isAuto ? '_byob_autoship' : '_byob_onetime'] = bundleId;
          attrs._byob_hero = self.cfg.heroStyle || 'classic'; // Shoplift hero test arm
          // ALO-99: preset attribution for revenue/AOV by starter bundle ("custom" = ?items= link)
          if (self.preset) {
            attrs._byob_preset = self.preset.handle;
            if (self.preset.edited) attrs._byob_preset_edited = '1';
          }
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: attrs }),
          }).catch(function () { /* tracking is best-effort; never block ATC */ });
        })
        .then(function () {
          if (self.cfg.ctaBehavior === 'checkout') {
            window.location.href = '/checkout';
            return;
          }
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          document.dispatchEvent(new CustomEvent('cart:open'));
          btn.textContent = original;
          self._adding = false;
        })
        .catch(function (err) {
          btn.textContent = original;
          self._adding = false;
          alert(err.message);
        });
    },
  };

  function init(scope) {
    (scope || document).querySelectorAll('[data-bundle-builder]').forEach(function (root) { new BundleBuilder(root); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  // Theme editor re-renders the section on every change; bind the fresh markup
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
